import React, { useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Box, 
  Typography, 
  Stack, 
  Paper,
  Alert,
  CircularProgress,
  InputAdornment, // Added
  IconButton // Added
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility'; // Added
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // Added
import useAuthStore from '../store/useAuthStore';

// Helper function to determine the redirect path based on user role
const getDashboardPath = (role) => {
  switch (role) {
    case 'RANTING':
      return '/ranting-dashboard';
    case 'MWC':
      return '/mwc-dashboard';
    case 'LAZISNU':
      return '/lazisnu-dashboard';
    default:
      return '/login'; // Fallback
  }
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false); // New state for password visibility
  
  const navigate = useNavigate();
  const { login, loading, error: authError } = useAuthStore();

  const handleClickShowPassword = () => setShowPassword((show) => !show); // Handler for password visibility
  const handleMouseDownPassword = (event) => { event.preventDefault(); }; // Prevents focus loss

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError(''); // Clear previous errors

    try {
      const user = await login(username, password);
      // On successful login, navigate to the role-specific dashboard
      if (user) {
        const path = getDashboardPath(user.role);
        navigate(path, { replace: true });
      }
    } catch (err) {
      // The error from the store is already a formatted message
      setFormError(err.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>LAZISNU</title>
        <meta name="description" content="Secure login portal for LazisNU administrators to access the Islamic financial management system." />
      </Helmet>

      <Box
        component="main"
        sx={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3 },
          backgroundImage: 'url(/images/img_design_de_borda_de_ondas_gradientes_psd_grtis_2.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: '450px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
          }}
        >
          {/* Header Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ mt: 2, fontWeight: 'bold' }}>
              LAZISNU
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Aplikasi Manajemen Keuangan
            </Typography>
          </Box>

          <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
            Login
          </Typography>

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
            <Stack spacing={2}>
              {(formError || authError) && (
                <Alert severity="error">{formError || authError?.message}</Alert>
              )}
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
              />

              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'} // Dynamic type
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                InputProps={{ // InputProps for password visibility toggle
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                  disabled={loading}
                />
              }
              label="Ingatkan saya"
              sx={{ mt: 1, mb: 2, alignSelf: 'flex-start' }}
            />

            <Box sx={{ position: 'relative' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={loading}
              >
                Login
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'primary.main',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Login;


